// H1 兼职申请页面（TDesign）：导入excel兼职名单 + 搜索(登录账号/审核状态/入职日期范围=create_time)
// + 新增/编辑/查看/重置账号密码 + 多选审核/软删除 + 分页
// 列表按创建时间倒序，新创建/新导入的兼职显示在最上面。
import React, { useState, useEffect, useCallback } from 'react';
import {
  Button, Tag, Input, Select, Table, Space, Upload, Dialog, MessagePlugin, DatePicker, Descriptions,
} from 'tdesign-react';
import { parttimeApi } from '../../api/parttime';
import { getPageSize } from '../../api/parttimePager';
import ParttimeUserFormModal from './ParttimeUserFormModal';

const ROLE_LABEL = { LEADER: '团长', PERSON: '个人', LEADER_SUB: '团长下个人' };
// 状态中文映射（后端存储英文枚举，前端回显中文）
const STATUS_LABEL = { UNREGISTERED: '未注册', PENDING: '待审核', APPROVED: '已通过', REJECTED: '已拒绝' };
// 状态背景色（TDesign Tag 主题色：未注册-灰 / 待审核-橙 / 已通过-绿 / 已拒绝-红）
const STATUS_THEME = { UNREGISTERED: 'default', PENDING: 'warning', APPROVED: 'success', REJECTED: 'danger' };
const PAGE_SIZE_KEY = 'page_size_H1';
const FILTER_OPTS = [
  { label: '全部', value: 'ALL' },
  { label: '未注册', value: 'UNREGISTERED' },
  { label: '待审核', value: 'PENDING' },
  { label: '已通过', value: 'APPROVED' },
  { label: '已拒绝', value: 'REJECTED' },
];
// 密码规则：大写+小写+数字，8~16位（与小程序注册/后端 validPassword 一致）
const PWD_RE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,16}$/;

/** 简化日期显示（2026-09-04T12:00:00 → 2026-09-04 12:00:00） */
const fmtDt = (d) => (d ? String(d).replace('T', ' ').slice(0, 19) : '');

export default function ParttimeAudit() {
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [keyword, setKeyword] = useState('');
  // 入职日期范围（创建时间 create_time 过滤）
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  // 查询强制刷新标记：保证无搜索条件时点击「查询」也能触发接口（避免 setState 相同引用被 React 跳过）
  const [reloadKey, setReloadKey] = useState(0);
  const [sel, setSel] = useState(() => new Set());   // 勾选的兼职 userId
  const [importFiles, setImportFiles] = useState([]); // 已导入文件（用于最右侧展示文件名）
  const [delDlg, setDelDlg] = useState(null);         // 删除确认弹窗（存 userIds）
  const [auditDlg, setAuditDlg] = useState(null);     // 审核弹窗：{ type: 'pass' } | { type: 'reject' }
  const [rejectReason, setRejectReason] = useState(''); // 审核不通过原因
  const [deleting, setDeleting] = useState(false);      // 删除确认请求中（按钮 loading）
  const [auditing, setAuditing] = useState(false);      // 审核提交请求中（按钮 loading）
  // 新增/编辑弹窗：null | { mode:'create' } | { mode:'edit', row }
  const [formDlg, setFormDlg] = useState(null);
  // 查看弹窗：null | { loading, data }（data = user + wageVersions + timeline）
  const [viewDlg, setViewDlg] = useState(null);
  // 重置账号密码弹窗：null | { row }
  const [resetDlg, setResetDlg] = useState(null);
  const [resetForm, setResetForm] = useState({ newAccount: '', newPassword: '', confirmPassword: '' });
  const [resetting, setResetting] = useState(false);   // 重置提交请求中

  useEffect(() => { getPageSize(PAGE_SIZE_KEY).then(setPageSize).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, size: pageSize };
      // 仅当关键字非空时才带上，避免序列化成 "keyword=undefined" 导致查询结果为空
      if (keyword && keyword.trim()) params.keyword = keyword.trim();
      if (filterStatus !== 'ALL') params.status = filterStatus;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const res = await parttimeApi.listUsers(params);
      setRows(res.rows || []); setTotal(res.total || 0);
    } catch (e) { MessagePlugin.error(e.message); } finally { setLoading(false); }
  }, [filterStatus, keyword, page, pageSize, reloadKey, startDate, endDate]);
  useEffect(() => { load(); }, [load]);
  // 查询：强制触发一次接口（reloadKey 递增，即使 page 已为 1 也会重新查询）
  const submitSearch = () => { setPage(1); setReloadKey(k => k + 1); };
  // 重置搜索条件：清空关键字/状态/日期，回到第一页并重新查询
  const resetSearch = () => {
    setKeyword(''); setFilterStatus('ALL'); setStartDate(''); setEndDate('');
    setPage(1); setReloadKey(k => k + 1);
  };
  useEffect(() => { setPage(1); }, [filterStatus]);

  // 打开查看弹窗：拉取 完整信息 + 工资版本历史 + USER时间线
  const openView = async (row) => {
    setViewDlg({ loading: true, data: null });
    try {
      const data = await parttimeApi.userDetail(row.id);
      setViewDlg({ loading: false, data });
    } catch (e) {
      MessagePlugin.error(e.message);
      setViewDlg(null);
    }
  };

  // 打开重置账号密码弹窗：账号回显当前登录账号
  const openReset = (row) => {
    setResetDlg(row);
    setResetForm({ newAccount: row.phone || '', newPassword: '', confirmPassword: '' });
  };
  // 保存重置：账号必填唯一（后端校验）；密码可选，填则需强度合法且两次一致
  const confirmReset = async () => {
    if (!resetDlg) return;
    const account = (resetForm.newAccount || '').trim();
    if (!account) { MessagePlugin.warning('登录账号不能为空'); return; }
    const pwd = resetForm.newPassword || '';
    if (pwd && !PWD_RE.test(pwd)) {
      MessagePlugin.warning('密码需由大写+小写+数字组成且长度8-16位'); return;
    }
    if (pwd && pwd !== resetForm.confirmPassword) {
      MessagePlugin.warning('两次输入的密码不一致'); return;
    }
    setResetting(true);
    try {
      await parttimeApi.resetUserAccount({
        userId: resetDlg.id, newAccount: account,
        newPassword: pwd || '', confirmPassword: pwd || '',
      });
      MessagePlugin.success('重置成功');
      setResetDlg(null);
      setReloadKey(k => k + 1); // 登录账号变化后刷新列表展示
    } catch (e) { MessagePlugin.error(e.message); } finally { setResetting(false); }
  };

  // 新增/编辑保存成功：新增置第一页（新兼职显示在最上面），编辑刷新当前页
  const onFormSaved = () => {
    const created = !!(formDlg && formDlg.mode === 'create');
    setFormDlg(null);
    setSel(new Set());
    if (created) setPage(1);
    setReloadKey(k => k + 1);
  };

  // TDesign Table 列定义：第一列为复选框；空值留空；末尾为 状态 + 操作列
  const columns = [
    { colKey: 'row-select', type: 'multiple', width: 48 },
    { colKey: 'entryTime', title: '入职时间', width: 110, cell: ({ row }) => row.entryTime || '' },
    { colKey: 'name', title: '姓名', width: 100, cell: ({ row }) => row.name || '' },
    { colKey: 'wechatCount', title: '几个微信', width: 90, cell: ({ row }) => (row.wechatCount == null ? '' : String(row.wechatCount)) },
    { colKey: 'wechatNick', title: '微信昵称', ellipsis: true, cell: ({ row }) => row.wechatNick || '' },
    { colKey: 'wechatId', title: '微信号', ellipsis: true, cell: ({ row }) => row.wechatId || '' },
    { colKey: 'addWage', title: '拉新添加工资', width: 110, cell: ({ row }) => (row.wage && row.wage.addWage != null ? row.wage.addWage : '') },
    { colKey: 'groupWage', title: '拉新进群工资', width: 110, cell: ({ row }) => (row.wage && row.wage.groupWage != null ? row.wage.groupWage : '') },
    { colKey: 'isFullFive', title: '是否满五天', width: 90, cell: ({ row }) => row.isFullFive || '' },
    { colKey: 'isWorking', title: '是否在做', width: 90, cell: ({ row }) => row.isWorking || '' },
    { colKey: 'applyNote', title: '备注', ellipsis: true, cell: ({ row }) => row.applyNote || '' },
    { colKey: 'serviceKf', title: '负责客服', ellipsis: true, cell: ({ row }) => row.serviceKf || '' },
    { colKey: 'phone', title: '本人登录账号', ellipsis: true, cell: ({ row }) => row.phone || '' },
    { colKey: 'role', title: '角色', width: 110, cell: ({ row }) => (ROLE_LABEL[row.role] ? <Tag theme="primary" variant="light">{ROLE_LABEL[row.role]}</Tag> : '') },
    { colKey: 'leaderLogin', title: "ta的团长的登录账号", ellipsis: true, cell: ({ row }) => row.leaderLogin || '' },
    { colKey: 'status', title: '状态', width: 90, cell: ({ row }) => (row.status
      ? <Tag theme={STATUS_THEME[row.status] || 'default'} variant="light">{STATUS_LABEL[row.status] || row.status}</Tag>
      : '') },
    { colKey: 'op', title: '操作', width: 220, cell: ({ row }) => (
      <Space size={0}>
        <Button theme="primary" variant="text" size="small" onClick={() => openView(row)}>查看</Button>
        <Button theme="primary" variant="text" size="small" onClick={() => setFormDlg({ mode: 'edit', row })}>编辑</Button>
        <Button theme="primary" variant="text" size="small" onClick={() => openReset(row)}>重置账号密码</Button>
      </Space>
    ) },
  ];

  // 分页/每页条数变化：切换每页条数时回到第一页，切换页码时更新当前页
  const onPageChange = (info) => {
    const size = info && info.pageSize;
    const cur = typeof info === 'number' ? info : (info && info.current ? info.current : page);
    if (size && size !== pageSize) {
      setPageSize(size);
      setPage(1); // 每页条数变化后回到第一页
    } else if (cur) {
      setPage(cur);
    }
  };

  // 导入兼职名单 excel（幂等 upsert；返回统计并提示未通过的行；上传解析期间显示 loading 反馈）
  const doImport = async (ctx) => {
    const raw = (ctx && ctx.raw) || (ctx && ctx.file && ctx.file.raw);
    if (!raw) { MessagePlugin.error('未获取到文件'); return { status: 'fail', error: '未获取到文件' }; }
    // 上传解析中显示 loading，避免用户无反馈等待
    const loadingInst = await MessagePlugin.loading('正在导入兼职名单，请稍候...');
    try {
      const r = await parttimeApi.importParttimeWorkers(raw);
      loadingInst.close();
      if (r.skippedCount > 0) {
        MessagePlugin.error(`有 ${r.skippedCount} 条因内容不完整未导入`);
        if (r.skipped && r.skipped.length) {
          MessagePlugin.error('未导入：' + r.skipped.join('；'));
        }
      } else {
        MessagePlugin.success('导入完成');
      }
      setPage(1);
      setReloadKey(k => k + 1); // 导入成功后强制刷新列表，让新数据立即可见
      return { status: 'success' };
    } catch (e) {
      loadingInst.close();
      MessagePlugin.error(e.message);
      return { status: 'fail', error: e.message };
    }
  };

  // 删除兼职人员（软删；后端校验其对账完成，存在未对账数据会拦截）
  const openDelDlg = () => {
    if (!sel.size) { MessagePlugin.warning('请先勾选兼职人员'); return; }
    setDelDlg([...sel]);
  };
  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await parttimeApi.deleteUsers(delDlg);
      MessagePlugin.success('删除成功');
      setDelDlg(null);
      setSel(new Set());
      setPage(1);
      setReloadKey(k => k + 1);
    } catch (e) { MessagePlugin.error(e.message); } finally { setDeleting(false); }
  };

  // 批量审核：勾选数据后统一通过/拒绝（未勾选提示）
  const openAudit = (type) => {
    if (!sel.size) { MessagePlugin.warning('请先勾选兼职人员'); return; }
    setRejectReason('');
    setAuditDlg({ type });
  };
  const confirmAudit = async () => {
    if (!auditDlg) return;
    const ids = [...sel];
    if (auditDlg.type === 'reject' && !rejectReason.trim()) {
      MessagePlugin.warning('请填写不通过原因'); return;
    }
    try {
      setAuditing(true);
      await parttimeApi.auditUsers(ids, auditDlg.type === 'pass', rejectReason.trim());
      MessagePlugin.success(auditDlg.type === 'pass' ? '审核通过' : '已拒绝');
      setAuditDlg(null);
      setSel(new Set());
      setPage(1);
      setReloadKey(k => k + 1);
    } catch (e) { MessagePlugin.error(e.message); } finally { setAuditing(false); }
  };

  return (
    <div className="pt-app">
      {/* 整体页面容器：导入 + 搜索 + 列表 为一个整体 */}
      <div className="pt-card" style={{ padding: 20 }}>
        {/* 导入兼职名单（文件名显示到最右侧，完整显示） */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 14, borderBottom: '1px solid #f0f0f0' }}>
          <span className="pt-title" style={{ margin: 0, lineHeight: '32px' }}>导入兼职名单</span>
          <Upload
            theme="file"
            accept=".xlsx,.xls"
            requestMethod={doImport}
            fileListDisplay={() => null}
            onChange={(files) => setImportFiles(files || [])}
          />
          <span style={{ color: '#999', fontSize: 12 }}>上传后按「记录ID」幂等更新，缺必填列/必填值的不入库</span>
          {(importFiles || []).length > 0 && (
            <span style={{ marginLeft: 'auto', color: '#333', fontSize: 13, whiteSpace: 'nowrap' }} title={importFiles.map(f => f.name).join('、')}>
              {importFiles.map(f => f.name).join('、')}
            </span>
          )}
        </div>

        {/* 搜索区 */}
        <div style={{ padding: '14px 0', borderBottom: '1px solid #f0f0f0' }}>
          <p className="pt-search-title">兼职申请</p>
          <div className="pt-search-row">
            <span className="pt-search-label">登录账号</span>
            <Input placeholder="手机号/微信号" value={keyword} onChange={(v) => setKeyword(v)} style={{ width: 180 }} clearable />
            <span className="pt-search-label">审核状态</span>
            <Select value={filterStatus} options={FILTER_OPTS} onChange={(v) => setFilterStatus(v)} style={{ width: 120 }} />
            <span className="pt-search-label">入职时间</span>
            <DatePicker format="YYYY-MM-DD" value={startDate} placeholder="开始日期" style={{ width: 140 }} onChange={(v) => setStartDate(v || '')} />
            <DatePicker format="YYYY-MM-DD" value={endDate} placeholder="结束日期" style={{ width: 140 }} onChange={(v) => setEndDate(v || '')} />
            <Button theme="primary" variant="text" size="small" loading={loading} onClick={submitSearch}>查询</Button>
            <Button theme="default" variant="text" size="small" onClick={resetSearch}>重置</Button>
            <Button theme="primary" variant="text" size="small" onClick={() => setFormDlg({ mode: 'create' })}>新增</Button>
            <Button theme="success" variant="text" size="small" onClick={() => openAudit('pass')}>审核通过</Button>
            <Button theme="danger" variant="text" size="small" onClick={() => openAudit('reject')}>审核不通过</Button>
            <Button theme="danger" variant="text" size="small" onClick={openDelDlg}>删除</Button>
          </div>
        </div>

        {/* 列表 */}
        <div style={{ paddingTop: 14 }}>
          <Table
            rowKey="id"
            data={rows}
            loading={loading}
            hover
            size="small"
            resizable
            columns={columns}
            scroll={{ x: 1800 }}
            selectedRowKeys={[...sel]}
            onSelectChange={(keys) => setSel(new Set(keys))}
            empty={<span>暂无数据</span>}
            pagination={{
              current: page, pageSize, total,
              onChange: onPageChange,
              totalContent: (t) => `共 ${t} 条数据`,
            }}
          />
        </div>
      </div>

      {/* 新增/编辑兼职人员弹窗（共用表单组件；工资变化后端自动生成新价格版本号并记时间线） */}
      <ParttimeUserFormModal
        visible={!!formDlg}
        mode={formDlg ? formDlg.mode : 'create'}
        initial={formDlg && formDlg.mode === 'edit' ? formDlg.row : null}
        onCancel={() => setFormDlg(null)}
        onSaved={onFormSaved}
      />

      {/* 查看弹窗：左列 兼职人员信息；中列 工资版本历史；右列 USER 操作时间线 */}
      <Dialog
        header="兼职人员详情" visible={!!viewDlg} width="1180px"
        closeOnOverlayClick={false}
        onClose={() => setViewDlg(null)}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button theme="default" variant="text" size="small" onClick={() => setViewDlg(null)}>关闭</Button>
            </Space>
          </div>
        }>
        {viewDlg && viewDlg.loading ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: '#999' }}>加载中...</div>
        ) : viewDlg && viewDlg.data ? (
          (() => {
            const u = viewDlg.data.user || {};
            const w = u.wage || {};
            const wageVersions = viewDlg.data.wageVersions || [];
            const timeline = viewDlg.data.timeline || [];
            return (
              <div style={{ display: 'flex', gap: 16, maxHeight: '62vh', overflow: 'hidden' }}>
                {/* 左列：兼职人员信息 */}
                <div style={{ flex: '1 1 40%', minWidth: 0, overflow: 'auto', paddingRight: 4 }}>
                  <p style={{ fontWeight: 600, margin: '0 0 10px' }}>兼职人员信息</p>
                  <Descriptions
                    column={1}
                    items={[
                      { label: '入职时间', content: u.entryTime || '' },
                      { label: '姓名', content: u.name || '' },
                      { label: '几个微信', content: u.wechatCount == null ? '' : String(u.wechatCount) },
                      { label: '微信昵称', content: u.wechatNick || '' },
                      { label: '微信号', content: u.wechatId || '' },
                      { label: '拉新添加工资', content: w.addWage == null ? '' : String(w.addWage) },
                      { label: '拉新进群工资', content: w.groupWage == null ? '' : String(w.groupWage) },
                      { label: '是否满五天', content: u.isFullFive || '' },
                      { label: '是否在做', content: u.isWorking || '' },
                      { label: '负责客服', content: u.serviceKf || '' },
                      { label: '本人登录账号', content: u.phone || '' },
                      { label: '角色', content: ROLE_LABEL[u.role] || u.role || '' },
                      { label: 'ta的团长', content: u.leaderId ? `${u.leaderName || ''}（${u.leaderLogin || ''}）` : '' },
                      { label: '状态', content: STATUS_LABEL[u.status] || u.status || '' },
                      { label: '备注', content: u.applyNote || '' },
                    ]}
                  />
                </div>

                {/* 中列：工资版本历史（按版本号倒序） */}
                <div style={{ flex: '1 1 31%', minWidth: 0, overflow: 'auto', borderLeft: '1px solid #f0f0f0', paddingLeft: 16 }}>
                  <p style={{ fontWeight: 600, margin: '0 0 10px' }}>工资版本历史</p>
                  {wageVersions.length > 0 ? (
                    wageVersions.map((v) => (
                      <div key={v.version} style={{ display: 'flex', gap: 8, padding: '4px 0', borderBottom: '1px dashed #f0f0f0', fontSize: 12 }}>
                        <span style={{ color: '#0052d9', whiteSpace: 'nowrap' }}>v{v.version}</span>
                        <span>添加 {v.addWage} 元 / 进群 {v.groupWage} 元</span>
                        <span style={{ color: '#888', whiteSpace: 'nowrap' }}>{fmtDt(v.effectiveTime)}</span>
                        {v.source === 'MANUAL' && <Tag theme="warning" variant="light" size="small">后台维护</Tag>}
                      </div>
                    ))
                  ) : <span style={{ color: '#bbb' }}>暂无工资版本</span>}
                </div>

                {/* 右列：USER 操作时间线 */}
                <div style={{ flex: '1 1 29%', minWidth: 0, overflow: 'auto', borderLeft: '1px solid #f0f0f0', paddingLeft: 16 }}>
                  <p style={{ fontWeight: 600, margin: '0 0 10px' }}>操作时间线</p>
                  {timeline.length > 0 ? (
                    timeline.map((l, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, padding: '5px 0', borderBottom: '1px dashed #f0f0f0' }}>
                        <span style={{ color: '#888', whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDt(l.time)}</span>
                        <span style={{ fontSize: 12, wordBreak: 'break-all' }}>{l.text}</span>
                      </div>
                    ))
                  ) : <span style={{ color: '#bbb' }}>暂无记录</span>}
                </div>
              </div>
            );
          })()
        ) : null}
      </Dialog>

      {/* 重置账号密码弹窗：账号默认回显当前登录账号；密码与小程序注册规则一致（可留空=不改密码） */}
      <Dialog header="重置账号密码" visible={!!resetDlg} width="480px" closeOnOverlayClick onClose={() => setResetDlg(null)}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button theme="default" variant="text" size="small" disabled={resetting} onClick={() => setResetDlg(null)}>取消</Button>
              <Button theme="primary" variant="text" size="small" loading={resetting} disabled={resetting} onClick={confirmReset}>保存</Button>
            </Space>
          </div>
        }>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <span style={{ width: 92, textAlign: 'right', color: '#374151', fontSize: 13, flexShrink: 0 }}>登录账号</span>
          <Input value={resetForm.newAccount} placeholder="兼职人员登录账号" onChange={(v) => setResetForm((f) => ({ ...f, newAccount: v }))} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <span style={{ width: 92, textAlign: 'right', color: '#374151', fontSize: 13, flexShrink: 0 }}>新密码</span>
          <Input type="password" value={resetForm.newPassword} placeholder="留空表示不修改密码" onChange={(v) => setResetForm((f) => ({ ...f, newPassword: v }))} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span style={{ width: 92, textAlign: 'right', color: '#374151', fontSize: 13, flexShrink: 0 }}>确认密码</span>
          <Input type="password" value={resetForm.confirmPassword} placeholder="再次输入新密码" onChange={(v) => setResetForm((f) => ({ ...f, confirmPassword: v }))} />
        </div>
        <p style={{ margin: '0 0 4px', color: '#888', fontSize: 12 }}>密码需由大写+小写+数字组成且长度8-16位（与小程序注册规则一致）。</p>
        <p style={{ margin: 0, color: '#e37318', fontSize: 12 }}>
          新登录账号不能与现有兼职重复。
        </p>
      </Dialog>

      {/* 删除确认弹窗（受控，点击遮罩/按钮均可关闭） */}
      <Dialog header="删除确认" visible={!!delDlg} width="420px" closeOnOverlayClick onClose={() => setDelDlg(null)}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button theme="default" variant="text" size="small" onClick={() => setDelDlg(null)}>取消</Button>
              <Button theme="danger" variant="text" size="small" loading={deleting} disabled={deleting} onClick={confirmDelete}>删除</Button>
            </Space>
          </div>
        }>
        <p style={{ margin: 0 }}>确定删除选中的 {delDlg ? delDlg.length : 0} 位兼职人员吗？</p>
        <p style={{ margin: '8px 0 0', color: '#888', fontSize: 12 }}>删除为软删除，其名下数据保留；若存在「已领取但未对账」的数据将无法删除。</p>
      </Dialog>

      {/* 审核通过确认弹窗 */}
      <Dialog header="审核通过" visible={!!auditDlg && auditDlg.type === 'pass'} width="400px" closeOnOverlayClick onClose={() => setAuditDlg(null)}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button theme="default" variant="text" size="small" onClick={() => setAuditDlg(null)}>取消</Button>
              <Button theme="primary" variant="text" size="small" loading={auditing} disabled={auditing} onClick={confirmAudit}>通过</Button>
            </Space>
          </div>
        }>
        <p style={{ margin: 0 }}>确定通过选中的 {sel.size} 位兼职申请吗？</p>
      </Dialog>

      {/* 审核不通过弹窗（必填原因） */}
      <Dialog header="审核不通过" visible={!!auditDlg && auditDlg.type === 'reject'} width="420px" closeOnOverlayClick onClose={() => setAuditDlg(null)}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button theme="default" variant="text" size="small" onClick={() => setAuditDlg(null)}>取消</Button>
              <Button theme="danger" variant="text" size="small" loading={auditing} disabled={auditing} onClick={confirmAudit}>拒绝</Button>
            </Space>
          </div>
        }>
        <p style={{ margin: '0 0 12px' }}>确定拒绝选中的 {sel.size} 位兼职申请吗？</p>
        <Input placeholder="请填写不通过原因（必填）" value={rejectReason} onChange={(v) => setRejectReason(v)} />
      </Dialog>
    </div>
  );
}
